import React, { useRef } from 'react';
import Logo from './Logo/Logo';
import SearchInput from './SearchInput/SearchInput';
import SquareBlock from './SquareBlock/SquareBlock';
import SaveContent from './SaveContent/SaveContent';
import SavedContentDropdown from '../SwitcherBlock/SavedAllContent/SavedContentDropdown';
import { SwitcherBlock } from '../SwitcherBlock/SwitcherBlock';
import { Icon } from '../shared/Icon/Icon';

import { useThemeStore } from '../../stores/themeStore';
import { useSearchStore } from '../../stores/searchStore';
import { useDropdownStore } from '../../stores/dropdownStore';
import useIsMobile from '../../hooks/useIsMobile';

import styles from './Header.module.less';
import { useTranslation } from 'react-i18next';
import { useMenuStateStore } from '../../stores/menuStore';
import { useYearsStore } from '../../stores/useYearStore';

const useClickOutside = (ref : React.RefObject<HTMLElement>, callback : () => void) => {
  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [ref, callback]);
};

const Header = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const { isOpen, closeDropdown } = useDropdownStore();
  const { isSearchVisible, toggleSearch, closeSearch } = useSearchStore();
  const { isMenuOpen, closeMenu } = useMenuStateStore();
  const { showMobileFilter } = useYearsStore();
  const isDisabled = isOpen || showMobileFilter;

  const containerRef = useRef(null);

  useClickOutside(containerRef, () => {
    if (isSearchVisible) {
      closeSearch();
    }
  });

  const renderSearchSection = () => {
    if (!isSearchVisible) return null;

    if (isMobile) {
      return <SearchInput toggleSearch={toggleSearch} />;
    }

    return (
      <div className={styles.desktopSearchWrapper}>
        <SearchInput toggleSearch={toggleSearch} />
      </div>
    );
  };

  const onMainSectionClick = (e: React.MouseEvent<HTMLDivElement>) => {
    toggleSearch();
    e.stopPropagation();
  };

  const onCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isMenuOpen) {
      closeMenu();
    } else {
      closeDropdown();
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.header__wrapper}>
        <div className={styles.header__mainSectionContainer}>
          <SquareBlock />
          <div ref={containerRef} className={styles.header__mainSectionWrapper}>
            {!isSearchVisible ? (
              <div
                className={styles.header__mainSection}
                onClick={onMainSectionClick}
              >
                <div className={styles.header__logoWrapper}>
                  <Logo />
                  {isOpen && (
                    <span className={styles.header__titleMenu}>
                      {t('saved')}
                    </span>
                  )}
                  {isMenuOpen && (
                    <span className={styles.header__titleMenu}>
                      {t('Menu')}
                    </span>
                  )}
                </div>
                <div className={styles.header__iconsBlock}>
                  {!isOpen && !isMenuOpen ? (
                    <div
                      className={styles.header__iconsWrapper}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <button
                        onClick={toggleSearch}
                        disabled={isDisabled}
                        className={styles.header__searchBtn}
                      >
                        <Icon
                          id="zoom"
                          className={`${styles.icon} ${theme !== 'light' && styles.iconLight}`}
                        />
                      </button>
                      <SavedContentDropdown theme={'light'} />
                    </div>
                  ) : (
                    <button
                      onClick={onCloseClick}
                      className={styles.header__searchBtn}
                    >
                      <Icon
                        id="close-dropdown"
                        className={`${styles.iconCross} ${theme !== 'light' && styles.iconLight}`}
                      />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              renderSearchSection()
            )}
          </div>
        </div>
        <SaveContent />
      </div>
      <SwitcherBlock />
    </header>
  );
};

export default Header;
